/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { cloneDeep, assign, get, omit, startCase, toLower } from 'lodash';

import { Audit } from '@/pages/audit_setup_wizard/types';

/* eslint-disable complexity */
export const prepareLoadedData = (audit: any): Audit => {
  const managedIn = {
    managedIn:
      audit.managedIn === 'jira'
        ? {
            label: 'Jira',
            value: 'JIRA',
          }
        : audit.managedIn === 'servicenow'
        ? {
            label: 'ServiceNow',
            value: 'SERVICENOW',
          }
        : {
            label: 'ZenGRC',
            value: 'ZENGRC',
          },
  };

  const auditType = {
    type: audit.internal
      ? {
          label: 'Internal',
          value: 'internal',
        }
      : {
          label: 'External',
          value: 'external',
        },
  };

  const assessors = get(audit, ['assessmentTemplate', 'assessors']);
  const assessorsProperty = assessors
    ? {
        assessors: {
          value: assessors,
          label: startCase(toLower(assessors)),
        },
      }
    : {};
  const verifiers = get(audit, ['assessmentTemplate', 'verifiers']);
  const verifiersProperty = verifiers
    ? {
        verifiers: {
          value: verifiers,
          label: startCase(toLower(verifiers)),
        },
      }
    : {};
  const assessmentTemplate = audit.assessmentTemplate
    ? { assessmentTemplate: assign({}, audit.assessmentTemplate, assessorsProperty, verifiersProperty) }
    : {};

  return assign(
    omit(audit, 'internal'),
    { relatedProgram: audit.relatedProgram || [] },
    managedIn,
    auditType,
    assessmentTemplate
  );
};
/* eslint-enable complexity */

export const prepareSavingData = (audit: Audit, jiraBus: any): any => {
  const auditCopy = <any>cloneDeep(audit);

  // Status
  delete auditCopy.status;

  // Storage
  if (auditCopy.managedIn.value === 'JIRA') {
    auditCopy.managedIn = 'jira';
    auditCopy.jira = jiraBus.jiraData;
    delete auditCopy.jira.isValid;

    // If Audit has been edited
    if (auditCopy.id >= 0) {
      // Delete these fields since they can't be edited
      delete auditCopy.jira.issueTypeId;
      delete auditCopy.jira.projectId;
      delete auditCopy.jira.auditIssue;
    }
  } else if (auditCopy.managedIn.value === 'SERVICENOW') {
    auditCopy.managedIn = 'servicenow';
  } else {
    auditCopy.managedIn = 'zengrc';
  }

  if (auditCopy.managedIn !== 'servicenow') {
    delete auditCopy.servicenow;
  }

  // Storage integration
  if (auditCopy.storage) {
    delete auditCopy.storage.name;
  }

  // Type
  auditCopy.internal = auditCopy.type.value === 'internal';
  delete auditCopy.type;

  // People
  auditCopy.auditManagers = auditCopy.auditManagers.map(m => m.value);
  auditCopy.auditors = auditCopy.auditors.map(a => a.value);

  // Program
  if (auditCopy.relatedProgram) {
    auditCopy.relatedProgram = auditCopy.relatedProgram.value;
  }

  // Assessment template
  // Temporary fix
  delete auditCopy.assessmentTemplate;

  // Mapped objects
  delete auditCopy.mappedRequests;
  delete auditCopy.mappedControls;
  delete auditCopy.mappedAssessments;

  return auditCopy;
};
